import {Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import { MAX_LENGTH_VALIDATOR_KEY, WidgetValidator } from '@pt/pt-models';
import {ValidatorGenerator} from '../../utils';

@Component({
  selector: 'pt-input-text',
  templateUrl: './input-text.component.html',
  styleUrls: ['./input-text.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputTextComponent)
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputTextComponent),
      multi: true
    }
  ]
})
export class InputTextComponent implements OnInit, OnDestroy,OnChanges, ControlValueAccessor {

  @Input() controlName: string;
  @Input() validators: WidgetValidator[];
  @Input() label!: string;
  @Input() fontWeightBold: boolean;
  @Input() placeholder: string;
  @Input() help: string;

  public maxTextLength?: number | string;
  public formGroup: FormGroup;
  public isChanged = false;
  public required: boolean;
  public inputId: string;
  private isDisabled = false;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  private onChanged: (val) => void;
  private onTouched: () => void;

  ngOnInit(): void {
    this.inputId = 'ptWInputText_' + this.controlName;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.validators) {
      this.setFormGroup(true);
    } else {
      this.setFormGroup(false);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  writeValue(obj: any): void {
    this.formGroup.controls.value.setValue(obj, {emitEvent: false});
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.formGroup.controls.value.disable({emitEvent: false});
    } else {
      this.formGroup.controls.value.enable({emitEvent: false});
    }
  }

  validate() {
    return this.formGroup.invalid ? {
      invalid: true
    } : null;
  }

  private setFormGroup(changeValidator?: boolean) {
    this.destroy$.next(true);

    const validator = ValidatorGenerator.generateValidator(this.validators);
    this.required = validator?.includes(Validators.required);
    const ml: WidgetValidator = this.validators?.find(v => v.key === MAX_LENGTH_VALIDATOR_KEY);
    if (ml && ml.args && ml.args.length === 1) {
      this.maxTextLength = ml.args[0];
    }
    const prevValue = this.formGroup ? this.formGroup.value.value: null;

    this.formGroup = new FormGroup({
      value: new FormControl(prevValue, validator)
    });

    if (this.isDisabled) {
      this.formGroup.disable({emitEvent: false});
    }

    this.formGroup.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(t => {
      this.isChanged = t?.value !== null;
      if (this.onChanged) {
        if (!!t && !!t.value && t.value.length > 0) {
          this.onChanged(t.value);
        } else {
          this.onChanged(null);
        }
      }
      if (this.onTouched) {
        this.onTouched();
      }
    });

    if (changeValidator) {
      this.isChanged = true;
      if (this.onChanged) {
        this.onChanged(this.formGroup.controls.value.value);
      }
      if (this.onTouched) {
        this.onTouched();
      }
    }
  }
}
